/**
 * web/src/screens/Notebook/giftflow/step1.tsx — Step 1, Occasion: upcoming
 * occasions with the date and days remaining right-aligned, then Just
 * because and Something else; Continue stays disabled until one is chosen
 * (card NYJEJRCBfBGQ).
 */
import { useState } from 'react';
import type { GiftFlowState } from './hooks';
import { daysUntil, fmtDate } from './types';

export function StepOccasion({ g }: { g: GiftFlowState }) {
  const [sel, setSel] = useState<{ label: string; date: string | null } | null>(null);
  const [custom, setCustom] = useState('');
  const [customOpen, setCustomOpen] = useState(false);

  const rows = g.occasions.map((o) => ({ key: o.key, label: o.label, date: o.date as string | null }));

  return (
    <section className="gf-step" data-testid="gift-step-1">
      <h4 className="gf-heading" data-testid="gift-step1-heading">What&rsquo;s the occasion?</h4>
      <p className="gf-muted">It sets the deadline, so I only show gifts that arrive in time.</p>

      <ul className="gf-occasions">
        {rows.map((r) => {
          const isOn = sel?.label === r.label && sel?.date === r.date;
          const days = r.date ? daysUntil(r.date) : null;
          return (
            <li key={r.key}>
              <button
                type="button"
                className={`gf-occasion${isOn ? ' is-on' : ''}`}
                data-testid={`gift-occasion-${r.key}`}
                aria-pressed={isOn}
                onClick={() => { setSel({ label: r.label, date: r.date }); setCustomOpen(false); }}
              >
                <span>{r.label}</span>
                {r.date && (
                  <span className="gf-occasion-when" data-testid={`gift-occasion-when-${r.key}`}>
                    {fmtDate(r.date)} · {days === 0 ? 'today' : `${days} days`}
                  </span>
                )}
              </button>
            </li>
          );
        })}
        <li>
          <button
            type="button"
            className={`gf-occasion${sel?.label === 'Just because' ? ' is-on' : ''}`}
            data-testid="gift-occasion-just-because"
            aria-pressed={sel?.label === 'Just because'}
            onClick={() => { setSel({ label: 'Just because', date: null }); setCustomOpen(false); }}
          >
            <span>Just because</span>
          </button>
        </li>
        <li>
          <button
            type="button"
            className={`gf-occasion${customOpen ? ' is-on' : ''}`}
            data-testid="gift-occasion-custom"
            aria-pressed={customOpen}
            onClick={() => { setCustomOpen(true); setSel({ label: custom.trim() || 'Something else', date: null }); }}
          >
            <span>Something else</span>
          </button>
          {customOpen && (
            <input
              className="gf-custom-input"
              data-testid="gift-occasion-custom-input"
              value={custom}
              placeholder="Name the occasion"
              aria-label="Name the occasion"
              onChange={(e) => {
                setCustom(e.target.value);
                setSel({ label: e.target.value.trim() || 'Something else', date: null });
              }}
            />
          )}
        </li>
      </ul>

      <p className="gf-muted" data-testid="gift-step1-hint">
        Just because has no deadline. I&rsquo;ll favour things that feel unexpected.
      </p>

      <button
        type="button"
        className="pear-btn pear-btn-mustard gf-continue"
        data-testid="gift-step1-continue"
        disabled={!sel || g.busy}
        onClick={() => sel && void g.continueWithOccasion(sel)}
      >
        Continue
      </button>
    </section>
  );
}
